import { createContext, useContext, useMemo, useState } from 'react';
import type { PreviewLanguage } from '../i18n/types';
import type { PreviewRegion } from '../theme/tokens';

type PreviewContextValue = {
    region: PreviewRegion;
    language: PreviewLanguage;
    setRegion: (region: PreviewRegion) => void;
    setLanguage: (language: PreviewLanguage) => void;
};

const PreviewContext = createContext<PreviewContextValue | null>(null);

type PreviewProviderProps = {
    /**
     * Initial region. The caller typically derives this from
     * `applicationSelectionStore.appTarget`. Once mounted, the region
     * managed here is independent — changing it inside the preview does
     * not write back to the global selection store, and changing the
     * global store does not retro-actively update the preview.
     *
     * That decoupling is intentional (CTCAN-117): the CMS user can
     * preview Circle K content while editing in QC scope without
     * polluting the global selector for other pages.
     */
    initialRegion: PreviewRegion;
    /**
     * Initial language. Defaults to `'en'` to match the prior behaviour
     * of `containers/acvt-page` which seeded its local lang state to
     * English.
     */
    initialLanguage?: PreviewLanguage;
    children: React.ReactNode;
};

/**
 * Owns the preview-local `{ region, language }` state and exposes setters
 * for the Customize app preview panel. Pages and components consume
 * those values through `usePreview()` and `useT()`.
 */
export const PreviewProvider = ({
    initialRegion,
    initialLanguage = 'en',
    children,
}: PreviewProviderProps) => {
    const [region, setRegion] = useState<PreviewRegion>(initialRegion);
    const [language, setLanguage] = useState<PreviewLanguage>(initialLanguage);

    const value = useMemo<PreviewContextValue>(
        () => ({ region, language, setRegion, setLanguage }),
        [region, language]
    );

    return (
        <PreviewContext.Provider value={value}>
            {children}
        </PreviewContext.Provider>
    );
};

/**
 * Read the active preview region and language, plus the setters that
 * back the Customize app preview panel.
 */
export const usePreview = (): PreviewContextValue => {
    const value = useContext(PreviewContext);
    if (!value) {
        throw new Error('usePreview must be used within a PreviewProvider');
    }
    return value;
};
